import React, { useMemo, memo } from 'react';

// Pre-generate stable particle positions outside the component
const generateParticles = (count) => {
  const particles = [];
  for (let i = 0; i < count; i++) {
    particles.push({
      id: i,
      top: `${(i * 17 + 7) % 100}%`,
      left: `${(i * 23 + 11) % 100}%`,
      duration: 5 + (i % 5),
      delay: (i % 30) / 10
    });
  }
  return particles;
};

const PARTICLES = generateParticles(40);

const GridBackground = memo(() => {
  const largeNodes = useMemo(() => [
    { top: '8%', left: '50%', size: 'w-16 h-16', delay: '0s', glow: 'shadow-[0_0_20px_rgba(59,130,246,0.3)]' },
    { top: '30%', right: '12%', size: 'w-20 h-20', delay: '1s', glow: 'shadow-[0_0_25px_rgba(59,130,246,0.35)]' },
    { top: '65%', left: '8%', size: 'w-18 h-18', delay: '2s', glow: 'shadow-[0_0_22px_rgba(59,130,246,0.3)]' },
    { top: '50%', left: '30%', size: 'w-16 h-16', delay: '3s', glow: 'shadow-[0_0_20px_rgba(59,130,246,0.3)]' },
    { bottom: '25%', right: '40%', size: 'w-18 h-18', delay: '4s', glow: 'shadow-[0_0_22px_rgba(59,130,246,0.3)]' }
  ], []);

  const scatteredNodes = useMemo(() => [
    { top: '8%', left: '15%', size: 'w-10 h-10', delay: '0s', animated: true, id: 'node-0' },
    { top: '12%', right: '25%', size: 'w-8 h-8', delay: '0.4s', animated: false, id: 'node-1' },
    { top: '18%', left: '35%', size: 'w-12 h-12', delay: '0.8s', animated: true, id: 'node-2' },
    { top: '22%', right: '12%', size: 'w-9 h-9', delay: '1.2s', animated: false, id: 'node-3' },
    { top: '32%', right: '45%', size: 'w-11 h-11', delay: '2s', animated: true, id: 'node-5' },
    { top: '38%', left: '60%', size: 'w-9 h-9', delay: '2.4s', animated: false, id: 'node-6' },
    { top: '42%', right: '8%', size: 'w-8 h-8', delay: '2.8s', animated: true, id: 'node-7' },
    { top: '48%', left: '20%', size: 'w-10 h-10', delay: '3.2s', animated: false, id: 'node-8' },
    { top: '52%', right: '65%', size: 'w-7 h-7', delay: '3.6s', animated: false, id: 'node-9' },
    { top: '58%', left: '42%', size: 'w-11 h-11', delay: '4s', animated: true, id: 'node-10' },
    { top: '62%', right: '18%', size: 'w-9 h-9', delay: '4.4s', animated: false, id: 'node-11' },
    { top: '68%', left: '12%', size: 'w-8 h-8', delay: '4.8s', animated: false, id: 'node-12' },
    { top: '72%', right: '35%', size: 'w-10 h-10', delay: '5.2s', animated: true, id: 'node-13' },
    { top: '78%', left: '55%', size: 'w-7 h-7', delay: '5.6s', animated: false, id: 'node-14' },
    { top: '15%', left: '70%', size: 'w-8 h-8', delay: '6.4s', animated: true, id: 'node-16' },
    { top: '35%', right: '28%', size: 'w-10 h-10', delay: '6.8s', animated: false, id: 'node-17' },
    { top: '45%', left: '78%', size: 'w-11 h-11', delay: '7.2s', animated: false, id: 'node-18' },
    { top: '65%', left: '25%', size: 'w-9 h-9', delay: '8s', animated: true, id: 'node-20' },
    { top: '25%', left: '48%', size: 'w-10 h-10', delay: '8.8s', animated: false, id: 'node-22' },
    { top: '5%', left: '82%', size: 'w-8 h-8', delay: '9.6s', animated: true, id: 'node-24' }
  ], []);

  return (
    <div className="fixed inset-0 w-full h-full overflow-hidden" 
         style={{ background: 'linear-gradient(to bottom, #0f172a, #0f172a)' }}>
      {/* Grid pattern */}
      <div className="absolute inset-0" 
           style={{
             backgroundImage: `
               linear-gradient(rgba(59, 130, 246, 0.08) 1px, transparent 1px),
               linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px)
             `,
             backgroundSize: '50px 50px',
             maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)'
           }} 
      />

      {/* Overlay gradient to fade nodes behind content */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900/20" style={{ zIndex: 1 }} />

      {/* Node network */}
      <div className="absolute inset-0" style={{ zIndex: 0 }}>
        {/* Central hub node removed for minimal design */}

        {/* Minimal scattered edge nodes with glowing effect */}
        {largeNodes.map((node, i) => (
          <div
            key={`large-${i}`}
            className={`absolute ${node.size} rounded-full bg-blue-500/5 border-2 border-blue-500/40 ${node.glow}`}
            style={{
              ...node,
              animation: 'pulse 6s ease-in-out infinite',
              animationDelay: node.delay
            }}
          />
        ))}

        {/* Random scattered nodes with glowing effect */}
        {scatteredNodes.map((node, i) => (
          <div
            key={`outer-${i}`}
            className={`absolute ${node.size} rounded-full bg-slate-700/8 border border-slate-600/25`}
            style={{
              ...node,
              animation: node.animated 
                ? `pulse 6s ease-in-out infinite, ${i % 2 === 0 ? 'glowPulse' : 'glowPulseStrong'} 4s ease-in-out infinite`
                : 'pulse 6s ease-in-out infinite',
              animationDelay: node.delay,
              boxShadow: '0 0 15px rgba(100, 116, 139, 0.2)'
            }}
          >
            <div className="absolute inset-1 rounded-full bg-slate-600/10" style={{ boxShadow: 'inset 0 0 10px rgba(100, 116, 139, 0.15)' }} />
          </div>
        ))}

        {/* Floating data particles - stable positions */}
        {PARTICLES.map((particle) => (
          <div
            key={particle.id}
            className="absolute w-1 h-1 bg-blue-400 rounded-full"
            style={{
              top: particle.top,
              left: particle.left,
              animation: `float ${particle.duration}s ease-in-out infinite`,
              animationDelay: `${particle.delay}s`,
              opacity: 0.5
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-10px); }
          75% { transform: translateY(-30px) translateX(5px); }
        }
        
        @keyframes glowPulse {
          0%, 100% { 
            box-shadow: 0 0 15px rgba(100, 116, 139, 0.2);
          }
          50% { 
            box-shadow: 0 0 30px rgba(59, 130, 246, 0.6), 0 0 50px rgba(59, 130, 246, 0.3);
          }
        }
        
        @keyframes glowPulseStrong {
          0%, 100% { 
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
          }
          50% { 
            box-shadow: 0 0 40px rgba(59, 130, 246, 0.8), 0 0 60px rgba(59, 130, 246, 0.4);
          }
        }
      `}</style>
    </div>
  );
});

GridBackground.displayName = 'GridBackground';

export default GridBackground;